<template>
    <div>
        <limitless-page-header title="Momsrapport" sub-title="Försäljning"></limitless-page-header>

        <div class="content">
            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-lg-6">
                            <div class="d-flex">
                                <div class="col-form-label" style="width: 100px;">Från</div>
                                <div style="width: 150px;">
                                    <v-select
                                        :options="years"
                                        v-model="filter.from_year"
                                        :clearable="false"
                                    ></v-select>
                                </div>
                                <div style="width: 100px;">
                                    <v-select
                                        :options="months"
                                        v-model="filter.from_month"
                                        :clearable="false"
                                    ></v-select>
                                </div>
                            </div>
                            <div class="d-flex">
                                <div class="col-form-label" style="width: 100px;">Till</div>
                                <div style="width: 150px;">
                                    <v-select
                                        :options="years"
                                        v-model="filter.to_year"
                                        :clearable="false"
                                    ></v-select>
                                </div>
                                <div style="width: 100px;">
                                    <v-select
                                        :options="months"
                                        v-model="filter.to_month"
                                        :clearable="false"
                                    ></v-select>
                                </div>
                            </div>
                        </div>
                        <div class="col-lg-6 text-right">
                            <limitless-button btn-class="btn-light" @click="exportExcel" icon="icon-file-excel">Exportera Excel</limitless-button>
                            <limitless-button btn-class="btn-light" @click="exportSKV(false)" icon="icon-database-export">Exportera SKV-fil för periodisk sammanställing EU</limitless-button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="card">
                <div class="table-responsive">
                    <table class="table table-bordered">
                        <thead>
                            <tr>
                                <th>Faktura</th>
                                <th>Datum</th>
                                <th v-for="account in accounts" :key="'th-' + account.number">
                                    {{ account.label }}<br>{{ account.number }}
                                </th>
                                <th>VatNr</th>
                                <th>OrderID</th>
                                <th>CompanyName</th>
                                <th>Checksum</th>
                            </tr>
                        </thead>
                        <tbody v-if="invoices">
                            <tr v-for="invoice in invoices.rows" :key="'i-' + invoice.InvoiceID">
                                <td>
                                    <router-link :to="{ name: 'invoice.edit', params: { id: invoice.InvoiceID } }">
                                        {{ invoice.OCR }}
                                    </router-link>
                                </td>
                                <td>{{ invoice.InvoiceDate }}</td>
                                <td
                                    v-for="account in accounts"
                                    :key="'i-' + invoice.InvoiceID + '-' + account.number"
                                    class="text-right"
                                >
                                    {{ formatNumber(invoice.accounts[account.number] ? invoice.accounts[account.number] : 0) }}
                                </td>
                                <td>{{ invoice.OrgNo }}</td>
                                <td>
                                    <router-link :to="{ name: 'cardorders.edit', params: { id: invoice.OrderID } }">{{ invoice.OrderID }}</router-link>
                                </td>
                                <td>
                                    <router-link :to="{ name: 'customers.edit', params: { id: invoice.CustomerID } }">{{ invoice.CompanyName }}</router-link>
                                </td>
                                <td :class="{ 'bg-danger-200': invoice.checksum !== 0 }">{{ invoice.checksum }}</td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <th colspan="2">Totalt:</th>
                                <th
                                    v-for="account in accounts"
                                    :key="'tf-' + account.number"
                                    class="text-right"
                                >
                                    {{ formatNumber((invoices && invoices.totals[account.number]) ? invoices.totals[account.number] : 0) }}
                                </th>
                                <th colspan="4"></th>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            <b-modal
                id="modal_skv_preview"
                title="SKV"
                ok-title="Ladda ner"
                cancel-title="Avbryt"
                @ok="exportSKV(true)"
                cancel-variant="light"
            >
                <div v-html="skv"></div>
            </b-modal>
        </div> <!-- /content -->
    </div>
</template>

<script>
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import apiClient from '@services/ApiClient';
import vSelect from 'vue-select';
import { formatNumber } from '@services/Helpers';
import LimitlessButton from '@components/Limitless/LimitlessButton.vue';
import { debounce } from 'lodash';
import BackendRepository from '@services/repositories/BackendRepository';

export default {
    components: {
        LimitlessButton,
        LimitlessPageHeader,
        vSelect,
    },
    data: () => ({
        loading: true,
        invoices: null,
        accounts: [],
        filter: {
            from_year: null,
            from_month: null,
            to_year: null,
            to_month: null,
        },
        skv: '',
    }),
    mounted() {
        this.getAccounts();
        const today = new Date();
        this.filter.from_year = today.getFullYear();
        this.filter.from_month = `${today.getMonth() + 1}`.padStart(2, '0');
        this.filter.to_year = today.getFullYear();
        this.filter.to_month = `${today.getMonth() + 1}`.padStart(2, '0');
        this.getData();
    },
    watch: {
        filter: {
            deep: true,
            handler: debounce(function filterHandler() {
                const vm = this;
                vm.getData();
            }),
        },
    },
    methods: {
        formatNumber,
        getData() {
            this.loading = true;
            apiClient.get('/vue/economy/report/vatsales', { params: this.filter })
                .then((response) => {
                    this.invoices = response.data;
                })
                .finally(() => {
                    this.loading = true;
                });
        },
        exportExcel() {
            const filter = { ...this.filter };
            Object.keys(filter).forEach((key) => {
                if (filter[key] === null) {
                    filter[key] = '';
                }
            });
            window.open('/economy/report/vat-sales/excel?' + new URLSearchParams(filter).toString());
        },
        exportSKV(download) {
            if (download) {
                window.open('/economy/report/vat-sales/skv?' + new URLSearchParams(this.filter).toString() + '&download=1');
            } else {
                apiClient.get('/economy/report/vat-sales/skv', { params: this.filter })
                    .then((response) => {
                        this.skv = response.data;
                        this.$root.$bvModal.show('modal_skv_preview');
                    });
            }
        },
        getAccounts() {
            BackendRepository.getAccounts().then((res) => {
                this.accounts = res;
            });
        },
    },
    computed: {
        old_system_url() {
            return window.config.app.old_system_url;
        },
        years() {
            const today = new Date();
            const arr = [];
            // eslint-disable-next-line no-plusplus
            for (let i = 2020; i <= today.getFullYear(); i++) {
                arr.push(i);
            }
            return arr;
        },
        months() {
            const arr = [];
            // eslint-disable-next-line no-plusplus
            for (let i = 1; i <= 12; i++) {
                arr.push(`${i}`.padStart(2, '0'));
            }
            return arr;
        },
    },
};

</script>
